/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { stripLocalePrefix } from "@cosmos/common-utils";
import { setCookieLocaleHandler } from "@wf-components/navigation-consumer";

import type { NavLocaleClickEvent } from "@wf-components/navigation-consumer";

/**
 * Normalizes a path to ensure it starts with a forward slash
 *
 * @param path - The path to normalize
 * @returns The path with a leading slash
 *
 * @example
 * normalizePath("about") // => "/about"
 * normalizePath("/about") // => "/about"
 */
export function normalizePath(path: string): string {
	return path.startsWith("/") ? path : `/${path}`;
}

/**
 * Removes locale prefix from path if present
 *
 * @param path - The path that may contain a locale prefix
 * @param locales - Array of supported locale codes
 * @returns The path without locale prefix
 *
 * @example
 * removeLocaleFromPath("/en-US/about", ["en-US", "de"]) // => "/about"
 * removeLocaleFromPath("/about", ["en-US", "de"]) // => "/about"
 */
export function removeLocaleFromPath(path: string, locales: string[]): string {
	if (!path || !locales?.length) return path || "/";

	const normalizedPath = normalizePath(path);

	for (const locale of locales) {
		if (normalizedPath.startsWith(`/${locale}/`)) {
			return normalizedPath.slice(locale.length + 1);
		}
		if (normalizedPath === `/${locale}`) {
			return "/";
		}
	}

	return normalizedPath;
}

/**
 * Checks if Next.js is running in development mode.
 *
 * @returns true if NODE_ENV is "development"
 */
export function isDevMode(): boolean {
	return process.env.NODE_ENV === "development";
}

/** Returns a navigation path with any supported locale prefix removed. */
export function getNavLocalePath(path: string, locales: string[]): string {
	return stripLocalePrefix(locales, normalizePath(path));
}

/**
 * Locale click handler for development mode.
 * Sets cookieSecure to false to allow cookies on http://localhost.epicgames-gamedev.ol.epicgames.net:3000.
 */
export const setCookieLocaleHandlerDev = (e: Event) => setCookieLocaleHandler(e, { cookieSecure: false });

/**
 * Extracts the target locale code from an `epic-nav-locale-click` event, preferring the
 * site-mapped code (`mappedLangCode`) over the raw nav code (`langCode`) because it
 * matches the vocabulary of the locales the site renders with. Shared so consumers
 * with a custom locale handler resolve the code one way instead of re-deriving it.
 */
export const getLocaleFromClickEvent = (e: Event): string | undefined => {
	const { detail } = e as CustomEvent<NavLocaleClickEvent>;
	return detail?.link?.mappedLangCode || detail?.link?.langCode || undefined;
};

/** Uses the locale cookie to switch language; permits HTTP cookies in development. */
export const getLocaleOnClick = (): ((e: Event) => Promise<void>) => {
	if (isDevMode()) return setCookieLocaleHandlerDev;

	return setCookieLocaleHandler;
};
